import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "./ui/avatar";
import { Separator } from "./ui/separator";
import {
    Heart,
    HeartOff,
    MapPin,
    Calendar,
    Star,
    Users,
    Activity as ActivityIcon,
    CheckCircle,
    ExternalLink,
} from "lucide-react";
import { apiService } from "../services/api.service";
import { Creator, User } from "../types/user";
import { Activity } from "../types/activity";

interface CreatorProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    creatorId: string;
    onFollowChange?: (
        organizerName: string,
        isFollowing: boolean,
    ) => void;
}

export function CreatorProfileModal({
    isOpen,
    onClose,
    creatorId,
    onFollowChange,
}: CreatorProfileModalProps) {
    const [creator, setCreator] = useState<Creator | null>(null);
    const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
    const navigate = useNavigate();

    const fetchCreator = async () => {
        const creator = await apiService.getCreatorById(creatorId);
        setCreator(creator);
    }

    const fetchRecentActivities = async () => {
        const activitiesByCreatorId = await apiService.getActivitiesByCreatorId(creatorId);
        setRecentActivities(activitiesByCreatorId.slice(0, 3));
    }

    const handleViewFullProfile = () => {
        onClose(); // Close the modal first
        navigate(`/creator/${encodeURIComponent(creator?.id || '')}`);
    };

    useEffect(() => {
        fetchCreator();
        fetchRecentActivities();
    }, [creatorId]);


    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="sr-only">
                        Creator Profile
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        View profile details and follow status for{" "}
                        {creator?.name}
                    </DialogDescription>
                </DialogHeader>

                {/* Fixed Header Section */}
                <div className="flex-shrink-0">
                    {/* Cover Image */}
                    <div className="relative -mx-6 -mt-10 h-32 overflow-hidden rounded-t-lg">
                        <img
                            // src={creator?.coverImage}
                            // TODO: add user.coverImage
                            src="https://playhub.sfo3.cdn.digitaloceanspaces.com/activity_image/user_profile_backgound.jpg"
                            alt={`${creator?.name} cover`}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>

                    {/* Profile Header */}
                    <div className="relative -mt-16 px-6">
                        <div className="flex items-end justify-between">
                            <div className="flex items-end space-x-4">
                                <Avatar className="w-24 h-24 border-4 border-background">
                                    <AvatarImage
                                        src={creator?.avatar}
                                        alt={creator?.name}
                                    />
                                    <AvatarFallback className="text-lg">
                                        {creator?.name.slice(0, 2).toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="pb-2">
                                    <div className="flex items-center space-x-2">
                                        <h2 className="text-2xl font-bold text-primary">
                                            {creator?.name}
                                        </h2>
                                        {creator?.isVerified && (
                                            <CheckCircle className="w-6 h-6 text-blue-500 fill-current" />
                                        )}
                                    </div>
                                    <p className="text-white/80">
                                        {creator?.type}
                                    </p>
                                </div>
                            </div>

                            {/* <Button
                                onClick={handleFollowToggle}
                                variant={isFollowing ? "secondary" : "default"}
                                className="flex items-center space-x-2"
                            >
                                {isFollowing ? (
                                    <>
                                        <Heart className="w-4 h-4 fill-current" />
                                        <span>Following</span>
                                    </>
                                ) : (
                                    <>
                                        <HeartOff className="w-4 h-4" />
                                        <span>Follow</span>
                                    </>
                                )}
                            </Button> */}
                            <Button>Follow</Button>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 mt-6">
                        <div className="bg-muted p-3 rounded-lg text-center">
                            <Users className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                            <p className="font-semibold">
                                {123}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Followers
                            </p>
                        </div>
                        <div className="bg-muted p-3 rounded-lg text-center">
                            <ActivityIcon className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                            <p className="font-semibold">
                                {123}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Activities
                            </p>
                        </div>
                        <div className="bg-muted p-3 rounded-lg text-center">
                            <Star className="w-5 h-5 mx-auto mb-1 text-yellow-500" />
                            <p className="font-semibold">
                                {123}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Rating
                            </p>
                        </div>
                        <div className="bg-muted p-3 rounded-lg text-center">
                            <Calendar className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                            <p className="font-semibold">
                                {123}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Upcoming
                            </p>
                        </div>
                    </div> */}
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-6 space-y-6">
                    {/* About Section */}
                    <div>
                        <h3 className="font-semibold mb-2">About</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                            {creator?.description || 'This person is too lazy to write a description...'}
                        </p>
                    </div>

                    {/* Location and Website */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center space-x-2 text-sm">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                            <span>{creator?.city || 'Somewhere'}, {creator?.state || 'On Earth'}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm">
                            <ExternalLink className="w-4 h-4 text-muted-foreground" />
                            <a
                                href={`https://${creator?.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline"
                            >
                                {creator?.website}
                            </a>
                        </div>
                    </div>

                    {/* Specialties */}
                    {/* <div>
                        <h3 className="font-semibold mb-2">Specialties</h3>
                        <div className="flex flex-wrap gap-2">
                            {[
                                "City Tours",
                                "Road Cycling",
                                "Mountain Biking",
                                "Bike Maintenance",
                            ].map((specialty) => (
                                <Badge
                                    key={specialty}
                                    variant="secondary"
                                    className="text-xs"
                                >
                                    {specialty}
                                </Badge>
                            ))}
                        </div>
                    </div> */}

                    <Separator />

                    {/* Recent Activities */}
                    <div>
                        <h3 className="font-semibold mb-3">
                            Recent Activities
                        </h3>
                        <div className="space-y-3">
                            {recentActivities.map(
                                (activity, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                                    >
                                        <div>
                                            <p className="font-medium text-sm">
                                                {activity.title}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {new Date(activity.dateTime).toLocaleDateString()} at {new Date(activity.dateTime).toLocaleTimeString()}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-medium">
                                                {/* {activity.participants}/{activity.maxParticipants} participants */}
                                                Capacity: {activity.maxParticipants}
                                            </p>
                                        </div>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>

                    {/* Member Since */}
                    <div className="text-center text-xs text-muted-foreground pt-4 border-t">
                        Member since{" "}
                        {new Date(creator?.createdAt || '').toLocaleDateString(
                            "en-US",
                            {
                                month: "long",
                                year: "numeric",
                            },
                        )}
                    </div>
                </div>

                {/* Fixed Footer with View Full Profile Button */}
                {/* <div className="flex-shrink-0 px-6 pt-4 pb-2 border-t bg-background"> */}
                <Button
                    onClick={handleViewFullProfile}
                    variant="outline"
                    className="w-full flex items-center justify-center space-x-2 font-medium"
                >
                    <ExternalLink className="w-4 h-4" />
                    <span>View Full Profile</span>
                </Button>
                {/* </div> */}
            </DialogContent>
        </Dialog>
    );
}